import axios from 'axios';
import CONFIG from '../config/config';

/**
 * Fetch the list of customers.
 * @param {string} subscriptionGuid - The unique identifier for the subscription.
 * @returns {Promise<Array>} - List of customers.
 */
export const fetchCustomers = async (subscriptionGuid) => {
    if (!subscriptionGuid || typeof subscriptionGuid !== 'string' || subscriptionGuid.trim() === '') {
        throw new Error('Invalid or empty subscriptionGuid provided');
    }

    try {
        const url = `${CONFIG.API.BASE_URL}/customers/list`;
        const response = await axios.get(url, {
            headers: {
                ...CONFIG.DEFAULT_HEADERS,
                'accessToken': CONFIG.API.ACCESS_TOKEN,
                'subscriptionGuid': subscriptionGuid,
            },
        });

        if (response.status === 200) {
            return response.data; // Customer records
        } else {
            console.error(`Error fetching customers: ${response.status} - ${response.data}`);
            throw new Error(`Error: ${response.status}`);
        }
    } catch (error) {
        if (error.response && error.response.status === 404) {
            console.warn('No customers found, returning empty array.');
            return [];
        }

        console.error('Error fetching customers:', error);
        throw error; // Re-throw the error for other status codes
    }
};

/**
 * Fetch a customer by their email.
 * @param {string} email - The email address of the customer.
 * @returns {Promise<Object>} - The customer data if found.
 */
export const getCustomerByEmail = async (email) => {
    if (!email || typeof email !== 'string' || !/\S+@\S+\.\S+/.test(email)) {
        throw new Error('Invalid email address provided');
    }

    try {
        const url = `${CONFIG.API.BASE_URL}/customers/getByEmail`;
        const response = await axios.get(url, {
            params: { email },
            headers: {
                ...CONFIG.DEFAULT_HEADERS,
                accessToken: CONFIG.API.ACCESS_TOKEN,
            },
        });

        if (response.status === 200) {
            return response.data.customer;
        } else {
            throw new Error(`Error: ${response.status} - ${response.data.message}`);
        }
    } catch (error) {
        console.error('Error fetching customer by email:', error);
        throw error;
    }
};

/**
 * Fetch a customer by their GUID.
 * @param {string} customerGuid - The unique identifier for the customer.
 * @returns {Promise<Object>} - The customer data if found.
 */
export const getCustomerByGuid = async (customerGuid) => {
    if (!customerGuid || typeof customerGuid !== 'string' || customerGuid.trim() === '') {
        throw new Error('Invalid or empty customerGuid provided');
    }

    try {
        if (!customerGuid || typeof customerGuid !== 'string') {
            throw new Error('Invalid or empty customerGuid provided');
        }

        const url = `${CONFIG.API.BASE_URL}/customers/getByGuid`;
        const response = await axios.get(url, {
            params: { customerGuid },
            headers: {
                ...CONFIG.DEFAULT_HEADERS,
                accessToken: CONFIG.API.ACCESS_TOKEN,
            },
        });

        if (response.status === 200) {
            return response.data.customer;
        } else {
            throw new Error(`Error: ${response.status} - ${response.data.message}`);
        }
    } catch (error) {
        console.error('Error fetching customer by GUID:', error);
        throw error;
    }
};

/**
 * Create a new customer.
 * @param {Object} customerData - The data for the new customer.
 * @param {string} subscriptionGuid - The subscription GUID for the customer.
 * @returns {Promise<Object>} - The created customer data.
 */
export const createCustomer = async (customerData) => {
    if (!customerData || typeof customerData !== 'object') {
        throw new Error('Invalid or missing customer data');
    }
    if (!customerData.email || !/\S+@\S+\.\S+/.test(customerData.email)) {
        throw new Error('A valid email is required to create a customer');
    }

    //console.log('Creating customer with data:', JSON.stringify(customerData, null, 2));

    try {
        const url = `${CONFIG.API.BASE_URL}/customers/create`;
        const response = await axios.post(url, customerData, {
            headers: {
                ...CONFIG.DEFAULT_HEADERS,
                'accessToken': CONFIG.API.ACCESS_TOKEN,
            },
        });

        if (response.status === 201) { // HTTP 201 for Created
            return response.data; // Created customer data
        } else {
            console.error(`Error creating customer: ${response.status} - ${response.data}`);
            throw new Error(`Error: ${response.message}`);
        }
    } catch (error) {
        if (error.response && error.response.status === 409) {
            // Capture the API message for duplicate email
            throw new Error(error.response.data.message);
        }
        throw new Error('Failed to create customer');
    }
};

/**
 * Update an existing customer.
 * @param {string} customerGuid - The unique identifier for the customer to be updated.
 * @param {Object} updatedFields - The fields to update in the customer record.
 * @returns {Promise<Object>} - The updated customer data.
 */
export const updateCustomer = async (customerGuid, updatedFields) => {
    if (!customerGuid || typeof customerGuid !== 'string' || customerGuid.trim() === '') {
        throw new Error('Invalid or empty customerGuid provided');
    }
    if (!updatedFields || typeof updatedFields !== 'object') {
        throw new Error('Invalid or missing updated fields');
    }

    try {
        const url = `${CONFIG.API.BASE_URL}/customers/updateFields`;
        const body = { updatedFields };

        const response = await axios.patch(url, body, {
            headers: {
                ...CONFIG.DEFAULT_HEADERS,
                'accessToken': CONFIG.API.ACCESS_TOKEN,
                'customerGuid': customerGuid,
            },
        });

        if (response.status === 200) {
            return response.data;
        } else {
            console.error(`Error updating customer: ${response.status} - ${response.data}`);
            throw new Error(`Error: ${response.status}`);
        }
    } catch (error) {
        console.error('Error updating customer:', error);
        throw error;
    }
};